import { useCallback, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import duration, { Duration, DurationUnitType } from 'dayjs/plugin/duration'
import zip from 'lodash/zip'
import useIsWindowVisible from 'hooks/useIsWindowVisible'
import isBeforeNow from 'utils/isBeforeNow'

dayjs.extend(duration)

const units: DurationUnitType[] = ['year', 'month', 'day', 'hour', 'minute', 'second']
const unitsAbbr: string[] = ['yr', 'mo', 'd', 'h', 'm', 's']
const formats: string[] = ['Y', 'M', 'D', 'HH', 'mm', 'ss']

type CountTo = string | number | Date
const defaultOnDone = () => undefined

const useCountdown = (countTo: CountTo, unitLimit = 3, onDone = defaultOnDone) => {
  const isWindowVisible = useIsWindowVisible()
  const [isDone, setIsDone] = useState(false)

  const getTimeRemaining = useCallback(() => {
    const timeDiff = getTimeDiff(countTo)
    const timeDiffValues = units.map((unit) => timeDiff.get(unit))

    const valueFormatTuples = zip(timeDiffValues, unitsAbbr, formats)
    const timeExcerpt = valueFormatTuples
      .filter(([value], index, arr) => value !== 0 || index > arr.length - 4)
      .splice(0, unitLimit) // Get Top 3 Significant Time (i.e. 0y 1m 1d 5h 10m 20s --> 1m 1d 5h)
      .map(([, unit, format]) => [unit, timeDiff.format(format)])

    const secondsRemaining = timeDiff.asSeconds()

    return { timeExcerpt, secondsRemaining, timeDiff }
  }, [countTo, unitLimit])

  const [timeRemaining, setTimeRemaining] = useState(getTimeRemaining)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    if (isWindowVisible && !isPaused && !isDone) {
      const interval = setInterval(() => {
        const newRemaining = getTimeRemaining()
        setTimeRemaining(newRemaining)
        if (newRemaining.secondsRemaining <= 0) {
          setIsDone(true)
        }
      }, 1000)
      return () => clearInterval(interval)
    }
    return null
  }, [getTimeRemaining, isWindowVisible, isPaused, isDone])

  useEffect(() => {
    if (isDone) {
      const timer = setTimeout(onDone, 1000)
      return () => clearTimeout(timer)
    }
    return () => undefined
  }, [isDone, onDone])

  const pause = useCallback(() => setIsPaused(true), [])
  const unpause = useCallback(() => setIsPaused(false), [])

  return { ...timeRemaining, pause, unpause }
}

export default useCountdown

export const useCountdownObj = (countTo: CountTo, onDone = defaultOnDone) => {
  const { timeExcerpt } = useCountdown(countTo, 3, onDone)
  return Object.fromEntries(timeExcerpt.map(([k, v]) => [k, parseInt(v)]))
}

export const useHourCountdown = (countTo: CountTo, onDone = defaultOnDone) => {
  const { timeExcerpt, timeDiff } = useCountdown(countTo, formats.length, onDone)
  const { m, s } = Object.fromEntries(timeExcerpt.map(([k, v]) => [k, parseInt(v)]))

  return { h: Math.floor(timeDiff.asHours()), m, s }
}

const getTimeDiff = (to: CountTo): Duration => {
  const now = dayjs()
  let future = dayjs(to)
  if (!future.isValid() || isBeforeNow(future)) future = now
  return dayjs.duration(future.diff(now))
}
